import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Lock, User, Eye, EyeOff, ArrowRight, AlertCircle, Shield } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) { setError('Please enter your username'); return; }
    if (!password) { setError('Please enter your password'); return; }

    setLoading(true);
    setTimeout(async () => {
      const result = await login(username.trim(), password);
      if (!result.success) {
        setError(result.error);
        setLoading(false);
      }
    }, 400);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.04) 1px, transparent 0)',
          backgroundSize: '48px 48px'
        }} />
        <div className="absolute -top-32 -right-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-[420px]">
        {/* Logo & branding */}
        <div className="text-center mb-8">
          <img src="/shivmaya-logo.svg" alt="ShivMaya Tech Solutions" className="h-24 w-auto mx-auto mb-5" />
          <h1 className="text-2xl font-bold text-white tracking-tight">MayaSoft ERP</h1>
          <p className="text-blue-200/70 text-sm mt-1.5">Enterprise Resource Planning</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl shadow-black/20 overflow-hidden">
          {/* Card header accent */}
          <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600" />

          <div className="p-8">
            <div className="mb-7">
              <h2 className="text-xl font-semibold text-gray-900">Sign in</h2>
              <p className="text-gray-400 text-sm mt-1">Enter your credentials to continue</p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-5 bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2.5 text-sm text-red-600 animate-[fadeIn_0.2s_ease-out]">
                <AlertCircle size={16} className="flex-shrink-0" />
                <span className="font-medium">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Username */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Username</label>
                <div className={`relative rounded-xl border-2 transition-all duration-200 bg-gray-50/50 ${
                  focusedField === 'username'
                    ? 'border-blue-500 bg-white ring-4 ring-blue-500/10'
                    : error && !username ? 'border-red-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <User size={17} className={`absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'username' ? 'text-blue-500' : 'text-gray-400'
                  }`} strokeWidth={2} />
                  <input
                    type="text"
                    value={username}
                    onChange={e => { setUsername(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('username')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-4 py-3 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none rounded-xl"
                    placeholder="Enter username"
                    autoFocus
                    autoComplete="username"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Password</label>
                <div className={`relative rounded-xl border-2 transition-all duration-200 bg-gray-50/50 ${
                  focusedField === 'password'
                    ? 'border-blue-500 bg-white ring-4 ring-blue-500/10'
                    : error && !password ? 'border-red-300' : 'border-gray-200 hover:border-gray-300'
                }`}>
                  <Lock size={17} className={`absolute left-3.5 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                    focusedField === 'password' ? 'text-blue-500' : 'text-gray-400'
                  }`} strokeWidth={2} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="w-full pl-11 pr-12 py-3 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none rounded-xl"
                    placeholder="Enter password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-all"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-sm hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 shadow-lg shadow-blue-600/30 hover:shadow-blue-600/45 active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-[18px] h-[18px]" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In</span>
                      <ArrowRight size={16} strokeWidth={2.5} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Card footer */}
          <div className="px-8 py-4 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-center gap-1.5 text-gray-400">
              <Shield size={13} strokeWidth={2.5} />
              <span className="text-xs">Secure encrypted connection</span>
            </div>
          </div>
        </div>

        {/* Bottom text */}
        <div className="text-center mt-6">
          <p className="text-blue-200/70 text-xs tracking-wide">Developed by <span className="font-semibold text-white">ShivMaya Tech Solutions</span></p>
        </div>
      </div>
    </div>
  );
}
